'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { Bookmark, ExternalLink, Share2 } from 'lucide-react';
import { BreadcrumbItem, FixedLayout } from '@/components/ui/layout/fixed-layout';

// Components
import { PressArticleContent } from '@/components/ui/press/press-article-content';
import { PressRelatedCollections } from '@/components/ui/press/press-related-articles';
import { OverlayText } from '../common/overlay-text';

// Animation variants for staggered fade-in
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Container variant for staggered children
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

// Section component with intersection observer
function Section({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
    >
      {children}
    </motion.div>
  );
}

// Animated title component
function AnimatedTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`mb-4 ${className}`}>
      <h3 className="inline-block text-[10px] uppercase font-mono tracking-wider relative overflow-hidden">
        {children}
      </h3>
    </div>
  );
}

// Types for the components
type ArticleContent = {
  type: 'paragraph' | 'image' | 'quote' | 'subheading';
  content?: string;
  src?: string;
  alt?: string;
  caption?: string;
  attribution?: string;
};

type ContentBlock = ArticleContent;

interface Author {
  name: string;
  role: string;
  image: string;
}

type RelatedCollection = {
  title: string;
  excerpt: string;
  publishDate: string;
  slug: string;
  coverImage: string;
  category?: string;
  isCollection?: boolean;
  href?: string;
};

interface ArticleData {
  title: string;
  subtitle: string;
  coverImage: string;
  publishDate: string;
  readTime: string;
  author: Author;
  tags: string[];
  content: ArticleContent[];
  relatedCollectionsData: RelatedCollection[];
  originalLink?: string;
  contentType: 'online' | 'video' | 'podcast';
  videoUrl?: string;
  podcastUrl?: string;
}

interface PressDetailClientProps {
  articleData: ArticleData;
  breadcrumbs: BreadcrumbItem[];
}

// Tag component for article tags
const ArticleTag = ({ tag }: { tag: string }) => (
  <span key={tag} className="text-[8px] px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-800 uppercase font-mono">
    {tag}
  </span>
);

// Article subtitle component
const ArticleSubtitle = ({ subtitle }: { subtitle: string }) => (
  <p className="text-sm text-black/80 mt-4 max-w-3xl">{subtitle}</p>
);

// Header component with tags, title, and subtitle
const ArticleHeader = ({ title, subtitle, tags }: { title: string; subtitle: string; tags: string[] }) => (
  <motion.div
    className="pb-4 mb-6"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="flex justify-between items-start">
      <div>
        {/* Header section */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <OverlayText className="w-[100%]" text={title} />
        </motion.div>

        <ArticleSubtitle subtitle={subtitle} />
        <div className="flex flex-wrap gap-2 mt-4">
          {tags.map(tag => (
            <ArticleTag key={tag} tag={tag} />
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

// Mobile cover image component
const MobileCoverImage = ({ src, alt }: { src: string; alt: string }) => (
  <div className="mb-8 relative aspect-[4/3] w-full overflow-hidden">
    <Image
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 768px) 100vw, 50vw"
      priority
      className="object-cover grayscale hover:grayscale-0 transition-all duration-500"
    />
  </div>
);

// Mobile details section component
const MobileDetailsSection = ({
  coverImage,
  title,
  author,
  publishDate,
  readTime,
  originalLink,
}: {
  coverImage: string;
  title: string;
  author: Author;
  publishDate: string;
  readTime: string;
  originalLink?: string;
}) => (
  <div className="block lg:hidden mb-8">
    <MobileCoverImage src={coverImage} alt={title} />

    <Section className="mb-8">
      <ArticleDetailsSection
        author={author}
        publishDate={publishDate}
        readTime={readTime}
        originalLink={originalLink}
      />
    </Section>
  </div>
);

// Author info component
const AuthorInfo = ({ author }: { author: Author }) => (
  <div className="flex items-center">
    {/* <div className="relative h-8 w-8 rounded-full overflow-hidden mr-3">
      <Image 
        src={author.image} 
        alt={author.name}
        fill
        sizes="32px"
        className="object-cover"
      />
    </div> */}
    <div>
      <div className="text-[10px] font-medium">{author.name}</div>
      <div className="text-[8px] text-black/60">{author.role}</div>
    </div>
  </div>
);

// Publisher info component
const PublisherInfo = ({ author }: { author: Author }) => (
  <div className="flex items-center">
    <div>
      <div className="text-[10px] font-medium">CNN INT</div>
      <div className="text-[8px] text-black/60">Publisher</div>
    </div>
  </div>
);

// Article metadata field component
const MetadataField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="text-black/50 uppercase text-[7px]">{label}</span>
    <span className="text-[8px]">{value}</span>
  </div>
);

// Article metadata component
const ArticleMetadata = ({ publishDate, readTime }: { publishDate: string; readTime: string }) => (
  <div className="flex gap-4 text-black/70">
    <MetadataField
      label="PUBLISHED"
      value={new Date(publishDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })}
    />
    <MetadataField label="READING TIME" value={readTime} />
  </div>
);

// Desktop featured image component
const FeaturedImage = ({ src, alt }: { src: string; alt: string }) => (
  <motion.div variants={fadeInUp}>
    <AnimatedTitle>[ FEATURED IMAGE ]</AnimatedTitle>
    <div className="relative aspect-[4/3] overflow-hidden w-full mb-4">
      <Image
        src={src}
        alt={alt}
        fill
        sizes="(max-width: 768px) 100vw, 40vw"
        priority
        className="object-cover grayscale hover:grayscale-0 transition-all duration-500"
      />
    </div>
  </motion.div>
);

// Article details section component
const ArticleDetailsSection = ({
  author,
  publishDate,
  readTime,
  originalLink,
}: {
  author: Author;
  publishDate: string;
  readTime: string;
  originalLink?: string;
}) => (
  <motion.div variants={fadeInUp}>
    <AnimatedTitle className="">[ ARTICLE DETAILS ]</AnimatedTitle>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-4">
        <PublisherInfo author={author} />
        <AuthorInfo author={author} />
      </div>
      <ArticleMetadata publishDate={publishDate} readTime={readTime} />
      <div className="text-[8px] ">
        <Link
          href={originalLink}
          target="_blank"
          className="flex items-center text-[8px] text-black/60 hover:text-black transition-colors border border-black/20 px-2 py-0.5"
        >
          <ExternalLink className="h-2.5 w-2.5 mr-1" />
          VIEW ORIGINAL
        </Link>
      </div>
    </div>
  </motion.div>
);

// Left panel details component
const LeftPanelDetails = ({
  author,
  publishDate,
  readTime,
  originalLink,
  coverImage,
  title,
}: {
  author: Author;
  publishDate: string;
  readTime: string;
  coverImage: string;
  title: string;
  originalLink?: string;
}) => (
  <div className="hidden lg:block mb-8">
    <Section>
      <ArticleDetailsSection
        author={author}
        publishDate={publishDate}
        readTime={readTime}
        originalLink={originalLink}
      />
    </Section>

    <Section className="mt-12">
      <FeaturedImage src={coverImage} alt={title} />
    </Section>
  </div>
);

// Action button component
const ActionButton = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <button className="flex items-center text-[10px] text-black/60 hover:text-black transition-colors">
    {icon}
    {label}
  </button>
);

// Article action container
const ActionContainer = ({ children }: { children: React.ReactNode }) => (
  <div className="flex space-x-4">{children}</div>
);

// Article actions component
const ArticleActions = ({ originalLink }: { originalLink?: string }) => (
  <motion.div variants={fadeInUp} className="flex justify-between items-center">
    <ActionContainer>
      <ActionButton icon={<Share2 className="h-3.5 w-3.5 mr-1.5" />} label="SHARE" />
      {/* <ActionButton icon={<Bookmark className="h-3.5 w-3.5 mr-1.5" />} label="SAVE" /> */}
    </ActionContainer>
    {originalLink && (
      <Link
        href={originalLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-[10px] text-black/60 hover:text-black transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
        VIEW ORIGINAL ARTICLE
      </Link>
    )}
  </motion.div>
);

// Left panel heading component
const LeftPanelHeading = ({ title, subtitle, tags }: { title: string; subtitle: string; tags: string[] }) => (
  <div className="bg-white z-10 pb-6">
    <ArticleHeader title={title} subtitle={subtitle} tags={tags} />
  </div>
);

// Left panel content component
const LeftPanelContent = ({ children }: { children: React.ReactNode }) => <div className="space-y-8">{children}</div>;

// Left panel fixed content component
const LeftPanel = ({
  title,
  subtitle,
  tags,
  children,
}: {
  title: string;
  subtitle: string;
  tags: string[];
  children: React.ReactNode;
}) => (
  <div className="flex-1 h-[calc(100dvh-107px)] lg:overflow-y-auto pr-0 lg:pr-5 scrollbar-hide">
    <LeftPanelHeading title={title} subtitle={subtitle} tags={tags} />
    <LeftPanelContent>{children}</LeftPanelContent>
  </div>
);

// CSS for scrollbar hiding
const ScrollbarHideStyles = () => (
  <style jsx global>{`
    .scrollbar-hide {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  `}</style>
);

// Right panel content wrapper
const RightPanelContent = ({ children }: { children: React.ReactNode }) => (
  <div className="pl-0 lg:pl-8 py-1 border-l border-zinc-100">{children}</div>
);

// Custom styles container for article content
const ArticleContentStylesContainer = ({ children }: { children: React.ReactNode }) => (
  <div
    className="prose prose-base max-w-none
    prose-headings:font-medium prose-headings:uppercase
    prose-img:my-6 prose-img:relative prose-img:w-full prose-img:overflow-hidden
    prose-figcaption:text-[9px] prose-figcaption:text-black/60 prose-figcaption:mt-2 prose-figcaption:font-mono
    prose-p:text-justify prose-p:text-[11px] leading-relaxed
    prose-h2:text-[14px] prose-h3:text-[13px] prose-h4:text-[12px]
    prose-blockquote:text-[11px]"
  >
    {children}
  </div>
);

// Article content section component
const ArticleContentSection = ({ content }: { content: ArticleContent[] }) => (
  <Section className="mb-10">
    <motion.div variants={fadeInUp}>
      <AnimatedTitle className="">[ ARTICLE CONTENT ]</AnimatedTitle>
      <ArticleContentStylesContainer>
        <PressArticleContent content={content as ContentBlock[]} />
      </ArticleContentStylesContainer>
    </motion.div>
  </Section>
);

// Related collections section component
const RelatedCollectionsSection = ({ collections }: { collections: RelatedCollection[] }) => {
  // Koleksiyon yoksa bölümü gösterme
  if (!collections || collections.length === 0) {
    return null;
  }

  return (
    <Section className="mb-10">
      <motion.div variants={fadeInUp}>
        <AnimatedTitle className="">[ RELATED COLLECTIONS ]</AnimatedTitle>
        <PressRelatedCollections articles={collections} />
      </motion.div>
    </Section>
  );
};

// Article actions section component
const ArticleActionsSection = ({ originalLink }: { originalLink?: string }) => (
  <Section className="border-t border-b border-zinc-100 py-4 mb-10">
    <ArticleActions originalLink={originalLink} />
  </Section>
);

// Right panel scrollable content component
const RightPanel = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full lg:w-1/2 h-[calc(100dvh-107px)]">
    <div className="static lg:sticky lg:top-0 lg:right-0 lg:h-[calc(100dvh-107px)] lg:overflow-y-auto pr-0 scrollbar-hide">
      <ScrollbarHideStyles />
      <RightPanelContent>{children}</RightPanelContent>
    </div>
  </div>
);

// Video player component
const VideoPlayer = ({ videoUrl, className = 'w-full mb-8' }: { videoUrl: string; className?: string }) => {
  // Check if it's a YouTube embed URL
  const isYoutubeEmbed = videoUrl.includes('youtube.com/embed/');
  // Check if it's a Vimeo embed URL
  const isVimeoEmbed = videoUrl.includes('player.vimeo.com/video/');

  if (isYoutubeEmbed || isVimeoEmbed) {
    // For embedded videos (YouTube, Vimeo)
    return (
      <div className={`${className} aspect-video`}>
        <iframe
          src={videoUrl}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  } else {
    // For direct video files
    return (
      <div className={`${className}`}>
        <video className="w-full" controls preload="metadata">
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }
};

// Audio player component
const AudioPlayer = ({ audioUrl, className = '' }: { audioUrl: string; className?: string }) => {
  return (
    <div className="w-full mb-8 bg-zinc-50 p-4 rounded-md">
      <h3 className="text-sm font-mono mb-2">PODCAST AUDIO</h3>
      <audio className="w-full" controls preload="metadata">
        <source src={audioUrl} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

// Media content component that shows video or audio based on content type
const MediaContent = ({
  contentType,
  videoUrl,
  podcastUrl,
  className = '',
}: {
  contentType: 'online' | 'video' | 'podcast';
  videoUrl?: string;
  podcastUrl?: string;
  className?: string;
}) => {
  if (contentType === 'video' && videoUrl) {
    return <VideoPlayer videoUrl={videoUrl} className={className} />;
  } else if (contentType === 'podcast' && podcastUrl) {
    return <AudioPlayer audioUrl={podcastUrl} className={className} />;
  }
  return null;
};

export const PressDetailClient = ({ articleData, breadcrumbs }: PressDetailClientProps) => {
  return (
    <FixedLayout breadcrumbs={breadcrumbs}>
      <div className="w-full py-2.5 px-[15px] font-mono text-[10px] h-full">
        {articleData.contentType === 'online' && (
          <div className="relative flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-y-auto">
            {/* Left fixed panel: Title, subtitle, details, featured image */}
            <LeftPanel title={articleData.title} subtitle={articleData.subtitle} tags={articleData.tags}>
              {/* Details and featured image */}
              <LeftPanelDetails
                author={articleData.author}
                publishDate={articleData.publishDate}
                readTime={articleData.readTime}
                coverImage={articleData.coverImage}
                title={articleData.title}
                originalLink={articleData.originalLink}
              />
            </LeftPanel>

            {/* Right scrollable panel: Article content, actions, related collection */}
            <RightPanel>
              {/* Mobile-only details and image component */}
              <MobileDetailsSection
                coverImage={articleData.coverImage}
                title={articleData.title}
                author={articleData.author}
                publishDate={articleData.publishDate}
                readTime={articleData.readTime}
                originalLink={articleData.originalLink}
              />

              {/* Article content with wrapper */}
              <ArticleContentSection content={articleData.content} />

              {/* Article actions - share, save */}
              <ArticleActionsSection originalLink={articleData.originalLink} />

              {/* Related collections */}
              <RelatedCollectionsSection collections={articleData.relatedCollectionsData} />
            </RightPanel>
          </div>
        )}

        {articleData.contentType !== 'online' && (
          <div className="relative flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-y-auto">
            <div className="bg-white w-full h-full flex flex-col">
              <motion.div
                className="mb-6 lg:max-w-[50%]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <OverlayText className="w-[100%]" text={articleData.title} />
              </motion.div>
              <div className="grid grid-cols-12 gap-4 flex-1">
                <div className="col-span-12 order-2 lg:order-1 lg:col-span-4 flex items-end justify-start h-full">
                  <div className="flex flex-col gap-4 justify-end h-full w-full items-start">
                    <AnimatedTitle className="">[ ARTICLE DETAILS ]</AnimatedTitle>
                    <div className="flex items-start flex-col lg:flex- justify-between mb-4 gap-4 w-full">
                      <div className="flex items-center gap-4">
                        <PublisherInfo author={articleData.author} />
                        <AuthorInfo author={articleData.author} />
                      </div>
                      <ArticleMetadata publishDate={articleData.publishDate} readTime={articleData.readTime} />
                      <div>
                        <motion.div
                          variants={fadeInUp}
                          className="flex justify-between items-center gap-4 py-2 m-1 border-t border-b border-zinc-100"
                        >
                          {articleData.originalLink && (
                            <Link
                              href={articleData.originalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-[10px] text-black/60 hover:text-black transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                              VIEW ORIGINAL ARTICLE
                            </Link>
                          )}
                          <ActionContainer>
                            <ActionButton icon={<Share2 className="h-3.5 w-3.5 mr-1.5" />} label="SHARE" />
                            {/* <ActionButton icon={<Bookmark className="h-3.5 w-3.5 mr-1.5" />} label="SAVE" /> */}
                          </ActionContainer>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-8 relative order-1 lg:order-2">
                  <Section className="flex items-end justify-end h-full">
                    <MediaContent
                      contentType={articleData.contentType}
                      videoUrl={articleData.videoUrl}
                      podcastUrl={articleData.podcastUrl}
                      className="w-full"
                    />
                  </Section>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FixedLayout>
  );
};

/* 

 Media content section
 {articleData.contentType !== 'online' && (
  <Section className="mb-10">
    <MediaContent
      contentType={articleData.contentType}
      videoUrl={articleData.videoUrl}
      podcastUrl={articleData.podcastUrl}
    />
  </Section>
)}

*/
